import GoogleAdsLogo from "../Assets/Images/logos_google-ads.svg"
import FacebookLogo from "../Assets/Images/logos_facebook.svg"
import LinkedInLogo from "../Assets/Images/logos_linkedin-icon.svg"
import BingLogo from "../Assets/Images/logos_bing.svg"
import GoogleAnalyticsLogo from "../Assets/Images/logos_google_analytics.webp"
import LookerLogo from "../Assets/Images/logos_looker.png"
import SalesforceLogo from "../Assets/Images/logos_salesforce.png"
import ShopifyLogo from "../Assets/Images/logos_shopify.png"
import AdjustLogo from "../Assets/Images/logos_adjust.png"
import BranchLogo from "../Assets/Images/logos_branch.png"
import MetadataLogo from "../Assets/Images/logos_metadata.svg"
import AppsflyerLogo from "../Assets/Images/logos_appsflyer.webp"
import DataBlendIcon from "../Assets/Icons/DataBlend.svg";
import TiktokLogo from "../Assets/ChannelIcons/tiktok.svg";
import Ga4Logo from "../Assets/ChannelIcons/google_analytics_4.svg";

class Channel {
    constructor(type, title, icon, subtext, isSupported = false, isBlend = false) {
        this.type = type;
        this.title = title;
        this.icon = icon;
        this.subtext = subtext;
        this.isSupported = isSupported;
        this.isBlend = isBlend;

        if (this.isSupported) {
            if (this.isBlend) {
                const transformerModule = require("../api/blends/transformer");
                this.properties = transformerModule.properties;
                this.transformer = Object.keys(transformerModule)
                    .filter(t => typeof transformerModule[t] === 'function')
                    .reduce((obj, t) => ({ ...obj, [t]: transformerModule[t] }), {});
            } else {
                const transformerModule = require(`../api/${this.type}/transformer`);
                this.properties = transformerModule.properties;
                this.transformer = Object.keys(transformerModule)
                    .filter(t => typeof transformerModule[t] === 'function')
                    .reduce((obj, t) => ({ ...obj, [t]: transformerModule[t] }), {});
                this.actions = {
                    connectAccount: { type: `${this.type}.CONNECT_ACCOUNT` },
                    disconnectAccount: { type: `${this.type}.DISCONNECT_ACCOUNT` },
                    removeAccountData: { type: `${this.type}.DISCONNECT_ACCOUNT_SUCCESS` },
                    getAccountSummary: { type: `${this.type}.GET_ACCOUNT_SUMMARY` },
                    getFields: { type: `${this.type}.GET_FIELDS` },
                    getColumns: (params) => ({ type: `${this.type}.GET_COLUMNS`, payload: params }),
                    getCurrency: (account) => ({ type: `${this.type}.GET_CURRENCY`, payload: account }),
                };
            }
        }
    }

    loadGrid() {
        if (this.isSupported && !this.isBlend) {
            this.grid = require(`../api/${this.type}/grid`).default;
            this.grid.forEach(tab => tab.grids.forEach(grid => {
                grid.tabId = tab.id;
                grid.charts.forEach(chart => chart.gridId = grid.id);
            }));
        }
    }

    updateGridFields(chart, tables, metrics, dimensions) {
        if (!this.isSupported) { return; }

        function getId(column) {
            if (column.dataSource) {
                return `${column.id}/${column.dataSource}`
            } else {
                return column.id;
            }
        }

        const tableMap = new Map();
        tables?.forEach(table => tableMap.set(table.id, table));

        const metricMap = new Map();
        if (Array.isArray(metrics)) {
            metrics.forEach(metric => metricMap.set(getId(metric), metric));
        } else {
            Object.entries(metrics).forEach(e => {
                const innerMap = new Map();
                e[1].forEach(metric => innerMap.set(getId(metric), metric));
                metricMap.set(e[0], innerMap);
            });
        }

        const dimensionMap = new Map();
        if (Array.isArray(dimensions)) {
            dimensions.forEach(dimension => dimensionMap.set(getId(dimension), dimension));
        } else {
            Object.entries(dimensions).forEach(e => {
                const innerMap = new Map();
                e[1].forEach(dimension => innerMap.set(getId(dimension), dimension));
                dimensionMap.set(e[0], innerMap);
            });
        }

        chart.table = tableMap.get(chart.table?.id);

        [...chart.leftMetrics, ...(chart.rightMetrics ?? [])].forEach(metric => {
            if (metric.metric && Object.keys(metric.metric).length <= 2) {
                if (chart.table) {
                    if (chart.dataLevel) {
                        metric.metric = metricMap.get(chart.table.id + ":" + chart.dataLevel)?.get(getId(metric.metric));
                    } else {
                        metric.metric = metricMap.get(chart.table.id)?.get(getId(metric.metric));
                    }
                } else {
                    metric.metric = metricMap.get(getId(metric.metric));
                }
            }
        });
        chart.dimensions = chart.dimensions?.map(dimension => {
            if (dimension && Object.keys(dimension).length <= 2) {
                if (chart.table) {
                    if (chart.dataLevel) {
                        return dimensionMap.get(chart.table.id + ":" + chart.dataLevel)?.get(getId(dimension));
                    }
                    return dimensionMap.get(chart.table.id)?.get(getId(dimension));
                } else {
                    return dimensionMap.get(getId(dimension));
                }
            }
            return dimension;
        });
    }

    getState(state) {
        return this.isBlend ? state.blends[this.type] ?? {} : state[this.type];
    }
}

export const ChannelType = {
    Default: "default",
    GoogleAnalytics: "google-analytics",
    ShopifyAds: "shopify-ads",
    Adjust: "adjust",
    Branch: "branch",
    Looker: "looker",
    Appsflyer: "appsflyer",
    Salesforce: "salesforce",
    Metadata: "metadata",
    GoogleAds: "google-ads",
    FacebookAds: "facebook-ads",
    LinkedInAds: "linkedin-ads",
    Bing: "bing-ads",
    TiktokAds: "tiktok-ads",
    GoogleAnalytics4: "google-analytics-4"
};

export const AdsList = [
    new Channel(
        ChannelType.GoogleAds,
        "Google Ads",
        GoogleAdsLogo,
        "Connect and source data from Google Ads",
        true,
    ),
    new Channel(
        ChannelType.FacebookAds,
        "Facebook Ads",
        FacebookLogo,
        "Connect and source data from Facebook Ads",
        true,
    ),
    new Channel(
        ChannelType.LinkedInAds,
        "LinkedIn Ads",
        LinkedInLogo,
        "Connect and source data from Linkedin Ads",
        true
    ),
    new Channel(
        ChannelType.Bing,
        "Bing Ads",
        BingLogo,
        "Connect and source data from Bing",
        true
    ),
    new Channel(
        ChannelType.TiktokAds,
        "Tiktok Ads",
        TiktokLogo,
        "Connect and source data from Tiktok Ads",
        true
    )
];

export const AttributionList = [
    new Channel(
        ChannelType.GoogleAnalytics,
        "Google Analytics",
        GoogleAnalyticsLogo,
        "Connect and source data from Google Analytics",
        true,
    ),
    new Channel(
        ChannelType.GoogleAnalytics4,
        "Google Analytics 4",
        Ga4Logo,
        "Connect and source data from Google Analytics",
        true,
    ),
    new Channel(
        ChannelType.ShopifyAds,
        "Shopify",
        ShopifyLogo,
        "Connect and source data from Shopify",
        true,
    ),
    new Channel(
        ChannelType.Appsflyer,
        "Appsflyer",
        AppsflyerLogo,
        "Connect and source data from Appsflyer",
        true
    ),
    new Channel(
        ChannelType.Adjust,
        "Adjust",
        AdjustLogo,
        "Connect and source data from Adjust",
        true
    ),
    new Channel(
        ChannelType.Branch,
        "Branch",
        BranchLogo,
        "Connect and source data from Branch",
    ),
    new Channel(
        ChannelType.Looker,
        "Looker",
        LookerLogo,
        "Connect and source data from Looker",
    ),
    new Channel(
        ChannelType.Salesforce,
        "Salesforce",
        SalesforceLogo,
        "Connect and source data from Salesforce",
    ),
    new Channel(
        ChannelType.Metadata,
        "Metadata",
        MetadataLogo,
        "Connect and source data from Metadata",
    ),
];

const allChannels = [...AdsList, ...AttributionList];


export const SupportedChannels = allChannels.filter(channel => channel.isSupported);

const channelMapping = new Map(allChannels.map(v => [v.type, v]));
export const GetChannel = (channelString) => {
    if (!channelString) {
        return null;
    } else if (channelString === ChannelType.Default) {
        return SupportedChannels[0];
    } else if (channelMapping.has(channelString)) {
        return channelMapping.get(channelString);
    } else if (typeof (channelString) === "object" && "id" in channelString && "name" in channelString) {
        return new Channel(channelString.id, channelString.name, DataBlendIcon, "Template for customized blended data sources", true, true);
    } else {
        return null;
    }
};

export const GetChannelSortOrder = (channelType) => allChannels.findIndex(channel => channel.type === channelType);

SupportedChannels.forEach(channel => channel.loadGrid());
